import { Button } from '@packages/shared/src/ui/Button/Button';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { defineMessages, useIntl } from 'react-intl';
import { MiniDVLayer } from '@packages/modules/src/MiniDVLayer/MiniDVLayer.client';
import { AddToBasketButtonTailwind } from '@packages/modules/src/AddToBasketButton/AddToBasketButtonTailwind.client';
import type { ButtonHTMLAttributes } from 'react';
import { useState } from 'react';
import { WishlistFragmentFragmentDoc } from '@packages/gql/generated/shopping/WishlistFragmentFragmentDoc';
import {
  type FragmentType,
  getFragmentData,
  makeFragmentData,
} from '@packages/gql/generated/shopping';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { BasketActionsFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketActionsFragmentFragmentDoc';
import { AddToBasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/AddToBasketFragmentFragmentDoc';
import type {
  GlycerinCartProduct,
  GlycerinWishListProduct,
} from '@packages/tracking/src/types/types';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { buttonStylingTailwind } from './Actions.styles';
import { GraphqlDocuments } from './mappedModeToDocument';

export interface BasketActionProps {
  data: FragmentType<typeof BasketActionsFragmentFragmentDoc>;
  glycerinProduct: GlycerinCartProduct | GlycerinWishListProduct;
  className?: string;
  galleryView?: boolean;
}

const messages = defineMessages({
  buttonTextShort: {
    id: 'BasketAction.ButtonText',
    defaultMessage: 'Warenkorb',
    description: 'this will be displayed on the button',
  },
  buttonTextLong: {
    id: 'BasketAction.ButtonText.Long',
    defaultMessage: 'in den Warenkorb',
    description: 'this will be displayed on the button',
  },
  successMessage: {
    id: 'BasketAction.SuccessMessage',
    defaultMessage: 'Der Artikel wurde erfolgreich in Ihren Warenkorb gelegt',
  },
  ariaLabel: {
    id: 'BasketAction.AreaLabel',
    defaultMessage: 'In den Warenkorb',
  },
});
/* GraphQL */ `
  fragment BasketActionsFragment on WishlistLineItem {
    sku
    akl
    isSoldOut
    price {
      currency
      saving {
        value
      }
      value
      valueOld
    }
    unspecified
  }
`;
export const BasketAction: React.FC<
  BasketActionProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>
> = ({ data, className, galleryView, glycerinProduct, onClick, ...props }) => {
  const [open, setOpen] = useState(false);
  const { locale } = useI18n();
  const { formatMessage } = useIntl();
  const { addNotification } = useNotification();
  const { isMobile } = useDeviceType();
  const { akl, isSoldOut, price, sku, unspecified } = getFragmentData(
    BasketActionsFragmentFragmentDoc,
    data,
  );

  const [wishListState, removeFromWishlist] = useAuthMutation(GraphqlDocuments.wishlist);
  const handleRemoveFromWishlist = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    setOpen(false);
    // grab original lineItemSku make call against backend to remove from wishlist
    // this is a callback function that will only occur when the user clicks to add to basket
    if (wishListState.fetching) return;
    removeFromWishlist({ locale, sku })
      .then((result) => {
        if (result.error) throw new Error('error');
        const removeFromWishlistResult = result.data?.removeFromWishlist;
        const wishlist = getFragmentData(WishlistFragmentFragmentDoc, removeFromWishlistResult);
        if (wishlist) {
          addNotification({
            severity: 'success',
            message: formatMessage(messages.successMessage),
            duration: 5000,
            onClose: () => {},
          });
        }
      })
      .catch(() => {
        addNotification({
          severity: 'error',
          message: 'Fehler beim Entfernen des Artikels',
          duration: 5000,
          onClose: () => {},
        });
      });
  };
  const handleOpenDialog = () => {
    setOpen(true);
    // this will fire without having to wait for the response from the backend, if you want to wait for the response use handleRemoveFromWishlist instead
    //   if (!state.fetching && lineItemId && isGlycerinCartProduct(glycerinProduct)) {
    //         dispatchGlycerin<GTMEventGlycerinRemoveFromCart>({
    //           event: 'RemoveFromCart',
    //           RemoveFromCartData: {
    //             removedProducts: [glycerinProduct],
    //           },
    //         });
    //       }
    //     });
    //   }
  };

  if (isSoldOut) return null;
  const mobileAvailableListView = isMobile && !galleryView;
  const customButtonText = (() => {
    if (galleryView) {
      return null;
    }
    if (mobileAvailableListView) {
      return messages.buttonTextLong;
    }
    return messages.buttonTextShort;
  })();
  // eslint-disable-next-line no-nested-ternary
  const variant = galleryView
    ? 'icon-only'
    : mobileAvailableListView
      ? 'color-button'
      : 'icon-flat';

  return !unspecified && price !== null ? (
    <AddToBasketButtonTailwind
      data={makeFragmentData(
        {
          akl,
          sku,
          availability: {
            isOrderable: !isSoldOut,
            state: !unspecified ? 'AVAILABLE' : 'UNSELECTED',
          },
          price: {
            currency: price.currency === 'EUR' || price.currency === 'CHF' ? price.currency : 'EUR',
            saving: price?.saving?.value !== undefined ? { value: price.saving.value } : null,
            value: price?.value,
            valueOld: price?.valueOld,
          },
          marketcode: null,
        },
        AddToBasketFragmentFragmentDoc,
      )}
      data-testid="basket-action"
      color="default"
      onClose={handleRemoveFromWishlist}
      iconClassName="size-4 md:size-3 text-text-dark"
      className={`${buttonStylingTailwind} ${className ?? ''} flex-row-reverse`}
      variant={variant}
      customButtonText={customButtonText}
      hideEndIcon={mobileAvailableListView}
      aria-label={formatMessage(messages.ariaLabel)}
    />
  ) : (
    <>
      <Button
        className={`${buttonStylingTailwind} ${className ?? ''}`}
        onClick={handleOpenDialog}
        variant={variant}
        color="default"
        data-testid="basket-action"
        size="base"
        aria-label={customButtonText === null ? formatMessage(messages.ariaLabel) : undefined}
        {...props}
      >
        {mobileAvailableListView ? null : <div className="icon-emp-cart size-4 md:size-3" />}
        {customButtonText !== null && formatMessage(customButtonText)}
      </Button>
      <MiniDVLayer
        sku={sku}
        akl={akl}
        open={open}
        quantity={1}
        onClick={handleRemoveFromWishlist}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
